import { Select, Space, Spin, Tag } from 'antd';
import { debounce } from 'lodash';
import { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { DEPARTMENT_LABEL } from '../labels';
import { StarAccountRow, StarGroup } from '../types';
import { extractArray } from '../utils';

// Giá trị ô chọn (labelInValue): giữ tên đã chọn kể cả khi danh sách tìm kiếm đổi.
export type PickedValue = { value: number; label: ReactNode } | undefined;

export interface RemoteOption {
  value: number;
  /** Nội dung dòng trong danh sách (có thể kèm thẻ, dòng phụ). */
  label: ReactNode;
  /** Chữ trơn hiện trong ô sau khi chọn. */
  name: string;
  disabled?: boolean;
}

interface Props<T> {
  value?: PickedValue;
  onChange?: (value: PickedValue, row?: T) => void;
  /** Tải danh sách theo chữ tìm ('' = danh sách mặc định khi mở ô). */
  fetchRows: (query: string) => Promise<T[]>;
  toOption: (row: T) => RemoteOption;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: CSSProperties;
}

/**
 * Ô chọn có tìm kiếm phía máy chủ: mở ô là tải ngay danh sách mặc định (không phải gõ trước như
 * UserPicker), gõ thì tìm lại sau 400ms. Dòng `disabled` vẫn hiện để người dùng biết vì sao không chọn được.
 */
function RemoteSearchSelect<T>({
  value,
  onChange,
  fetchRows,
  toOption,
  placeholder,
  allowClear = true,
  disabled,
  size = 'middle',
  style,
}: Props<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const fetchRef = useRef(fetchRows);
  fetchRef.current = fetchRows;

  const load = useMemo(
    () => async (query: string) => {
      seq.current += 1;
      const id = seq.current;
      setLoading(true);
      try {
        const next = await fetchRef.current(query.trim());
        if (id === seq.current) setRows(next);
      } catch {
        if (id === seq.current) setRows([]);
      } finally {
        if (id === seq.current) setLoading(false);
      }
    },
    []
  );
  const debouncedLoad = useMemo(() => debounce(load, 400), [load]);
  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

  const options = rows.map(toOption);

  return (
    <Select<PickedValue>
      showSearch
      labelInValue
      filterOption={false}
      optionLabelProp="name"
      value={value}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      size={size}
      style={{ width: '100%', ...style }}
      options={options}
      onSearch={debouncedLoad}
      onOpenChange={(open) => {
        if (open) load('');
      }}
      onChange={(next) => {
        const picked = next?.value;
        const index = options.findIndex((o) => o.value === picked);
        onChange?.(
          next
            ? { value: next.value, label: index >= 0 ? options[index].name : next.label }
            : undefined,
          index >= 0 ? rows[index] : undefined
        );
      }}
      notFoundContent={loading ? <Spin size="small" /> : 'Không tìm thấy.'}
    />
  );
}

export default RemoteSearchSelect;

// ——— Chọn người theo ví Sao (GET /stars/admin/accounts?group=&q=) ———

const accountName = (row: StarAccountRow) =>
  row.nickName || row.username || row.email || `#${row.userId}`;

export function accountFetcher(group: StarGroup) {
  return async (query: string) => {
    const res: any = await AxiosUtils.get(API.starsAccounts, {
      group,
      ...(query ? { q: query } : {}),
      page: 1,
      limit: 20,
    });
    return extractArray<StarAccountRow>(res);
  };
}

interface AccountSelectProps {
  group: StarGroup;
  value?: PickedValue;
  onChange?: (value: PickedValue, row?: StarAccountRow) => void;
  placeholder?: string;
  /** userId → lý do không chọn được (vd. "Bạn là người tạo phiếu"). */
  blocked?: Record<number, string>;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: CSSProperties;
}

/** Ô chọn người trong một nhóm Sao; ví bị khoá hiện mờ, không chọn được. */
export const StarAccountSelect = ({
  group,
  value,
  onChange,
  placeholder = 'Gõ tên để tìm',
  blocked,
  allowClear,
  size,
  style,
}: AccountSelectProps) => {
  const fetchRows = useMemo(() => accountFetcher(group), [group]);
  return (
    <RemoteSearchSelect<StarAccountRow>
      value={value}
      onChange={onChange}
      fetchRows={fetchRows}
      placeholder={placeholder}
      allowClear={allowClear}
      size={size}
      style={style}
      toOption={(row) => {
        const frozen = row.status === 'FROZEN';
        const reason = blocked?.[row.userId] || (frozen ? 'Ví Sao đang khoá' : undefined);
        const sub = [
          row.username,
          row.department && (DEPARTMENT_LABEL[row.department] || row.department),
        ]
          .filter(Boolean)
          .join(' · ');
        return {
          value: row.userId,
          name: accountName(row),
          disabled: !!reason,
          label: (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <Space size={6}>
                <span style={{ fontWeight: 600 }}>{accountName(row)}</span>
                {reason && <Tag style={{ marginInlineEnd: 0 }}>{reason}</Tag>}
              </Space>
              {sub && <span style={{ color: colors.textSecondary, fontSize: 12 }}>{sub}</span>}
            </div>
          ),
        };
      }}
    />
  );
};
